import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  Failure,
  Field,
  Loading,
  Modal,
  Notice,
} from '../components/ui.js';
import { PERMISSION_LABELS, ROLE_LABELS, formatDateTime } from '../lib/format.js';

interface Membership {
  membership_id: string;
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  permissions: string[];
  school_scope: string[];
  disabled: boolean;
  last_login_at: string | null;
}

const EMERGENCY_PERMISSIONS = ['incident.activate', 'incident.clear'];
const OPTIONAL_PERMISSIONS = [...EMERGENCY_PERMISSIONS];

export function UsersPage(): JSX.Element {
  const { can, me } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Membership | null>(null);
  const [removing, setRemoving] = useState<Membership | null>(null);
  const [credential, setCredential] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => get<{ items: Membership[]; total: number }>('/users?page_size=100'),
  });

  const schools = useQuery({
    queryKey: ['schools', 'all'],
    queryFn: () => get<{ items: Array<{ id: string; name: string }> }>('/schools?page_size=500'),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      post<{ email: string; initial_password: string | null }>('/users', body),
    onSuccess: (res) => {
      setShowForm(false);
      setError(null);
      if (res.initial_password) setCredential({ email: res.email, password: res.initial_password });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; body: Record<string, unknown> }) =>
      patch(`/users/${vars.id}`, vars.body),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => post<{ temporary_password: string }>(`/users/${id}/reset-password`),
    onSuccess: (res, id) => {
      const user = users.data?.items.find((u) => u.membership_id === id);
      setCredential({ email: user?.email ?? '', password: res.temporary_password });
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/users/${id}`),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!can('user.read')) {
    return (
      <Card title="المستخدمون">
        <Notice tone="warn">ليست لديك صلاحية عرض المستخدمين.</Notice>
      </Card>
    );
  }

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        title="المستخدمون والصلاحيات"
        subtitle={`${users.data?.total ?? 0} عضوية`}
        actions={
          can('user.manage') ? (
            <button className="primary" onClick={() => setShowForm(true)}>
              إضافة مستخدم
            </button>
          ) : null
        }
      >
        <Notice tone="info">
          صلاحيتا تفعيل الإنذار وإلغائه مستقلتان عن الدور العام ومعطّلتان افتراضيًا. لا يمنحهما أي
          دور تلقائيًا، حتى مدير المديرية.
        </Notice>

        {users.isLoading ? (
          <Loading />
        ) : users.error ? (
          <Failure error={users.error} onRetry={() => void users.refetch()} />
        ) : users.data!.items.length === 0 ? (
          <Empty title="لا يوجد مستخدمون" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>المستخدم</th>
                  <th>الدور</th>
                  <th>صلاحيات خاصة</th>
                  <th>النطاق</th>
                  <th>آخر دخول</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data!.items.map((u) => (
                  <tr key={u.membership_id}>
                    <td>
                      {u.display_name}
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{u.email}</div>
                      {u.disabled ? (
                        <Badge tone="danger" icon="✖">
                          معطّل
                        </Badge>
                      ) : null}
                      {u.user_id === me?.user.id ? (
                        <Badge tone="muted">أنت</Badge>
                      ) : null}
                    </td>
                    <td>{ROLE_LABELS[u.role] ?? u.role}</td>
                    <td>
                      {u.permissions.length === 0 ? (
                        <span style={{ color: 'var(--muted)' }}>—</span>
                      ) : (
                        <div className="pill-list">
                          {u.permissions.map((p) => (
                            <Badge
                              key={p}
                              tone={EMERGENCY_PERMISSIONS.includes(p) ? 'danger' : 'info'}
                              icon={EMERGENCY_PERMISSIONS.includes(p) ? '⚠' : undefined}
                            >
                              {PERMISSION_LABELS[p] ?? p}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {u.school_scope.length === 0
                        ? 'كل المديرية'
                        : `${u.school_scope.length} مدرسة`}
                    </td>
                    <td style={{ fontSize: 12.5 }}>{formatDateTime(u.last_login_at)}</td>
                    <td className="actions">
                      {can('user.manage') ? (
                        <>
                          <button className="ghost" onClick={() => setEditing(u)}>
                            تعديل
                          </button>
                          <button
                            className="ghost"
                            disabled={resetPassword.isPending}
                            onClick={() => resetPassword.mutate(u.membership_id)}
                          >
                            إعادة تعيين كلمة المرور
                          </button>
                          <button className="ghost" onClick={() => setRemoving(u)}>
                            إزالة
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showForm ? (
        <UserForm
          schools={schools.data?.items ?? []}
          busy={create.isPending}
          onCancel={() => setShowForm(false)}
          onSubmit={(body) => create.mutate(body)}
        />
      ) : null}

      {editing ? (
        <UserForm
          initial={editing}
          schools={schools.data?.items ?? []}
          busy={update.isPending}
          onCancel={() => setEditing(null)}
          onSubmit={(body) => update.mutate({ id: editing.membership_id, body })}
        />
      ) : null}

      {credential ? (
        <Modal
          title="كلمة مرور مؤقتة"
          onClose={() => setCredential(null)}
          footer={<button onClick={() => setCredential(null)}>إغلاق</button>}
        >
          <Notice tone="warn">
            تظهر هذه الكلمة مرة واحدة. سلّمها للمستخدم عبر قناة آمنة واطلب تغييرها عند أول دخول.
          </Notice>
          <div style={{ marginBottom: 8 }}>{credential.email}</div>
          <div
            className="mono"
            style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 8, userSelect: 'all' }}
          >
            {credential.password}
          </div>
        </Modal>
      ) : null}

      {removing ? (
        <ConfirmDialog
          title="إزالة العضوية"
          danger
          busy={remove.isPending}
          confirmLabel="إزالة"
          message={
            <>
              ستُزال عضوية «{removing.email}» من هذه المديرية وتُنهى جلساته فورًا. لن يُحذف حسابه من
              المنصة.
            </>
          }
          onCancel={() => setRemoving(null)}
          onConfirm={() => remove.mutate(removing.membership_id)}
        />
      ) : null}
    </>
  );
}

function UserForm({
  initial,
  schools,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: Membership;
  schools: Array<{ id: string; name: string }>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}): JSX.Element {
  const editing = Boolean(initial);
  const [form, setForm] = useState({
    email: initial?.email ?? '',
    display_name: initial?.display_name ?? '',
    role: initial?.role ?? 'VIEWER',
    permissions: initial?.permissions ?? [],
    school_scope: initial?.school_scope ?? [],
    disabled: initial?.disabled ?? false,
  });

  return (
    <Modal
      title={editing ? `تعديل صلاحيات ${initial!.email}` : 'إضافة مستخدم'}
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button
            className="primary"
            disabled={busy || (!editing && (!form.email || !form.display_name))}
            onClick={() =>
              onSubmit(
                editing
                  ? {
                      role: form.role,
                      permissions: form.permissions,
                      school_scope: form.school_scope,
                      disabled: form.disabled,
                    }
                  : {
                      email: form.email.trim(),
                      display_name: form.display_name.trim(),
                      role: form.role,
                      permissions: form.permissions,
                      school_scope: form.school_scope,
                    },
              )
            }
          >
            {busy ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </>
      }
    >
      {!editing ? (
        <>
          <Field label="الاسم">
            <input
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            />
          </Field>
          <Field label="البريد الإلكتروني" hint="ستُولَّد كلمة مرور مؤقتة تظهر مرة واحدة">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>
        </>
      ) : null}

      <Field label="الدور">
        <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
          <option value="TENANT_ADMIN">{ROLE_LABELS.TENANT_ADMIN}</option>
          <option value="TENANT_OPERATOR">{ROLE_LABELS.TENANT_OPERATOR}</option>
          <option value="SCHOOL_ADMIN">{ROLE_LABELS.SCHOOL_ADMIN} — مدير مدرسة: لوحة المدرسة مع أزرار الجرس</option>
          <option value="VIEWER">{ROLE_LABELS.VIEWER} — مشاهدة فقط (لوحة المدرسة إن حُدد نطاق مدرسة)</option>
        </select>
      </Field>

      <Field label="صلاحيات خاصة" hint="لا يمنحها أي دور تلقائيًا">
        {OPTIONAL_PERMISSIONS.map((p) => (
          <label key={p} style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
            <input
              type="checkbox"
              checked={form.permissions.includes(p)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  permissions: e.target.checked
                    ? [...f.permissions, p]
                    : f.permissions.filter((x) => x !== p),
                }))
              }
            />
            <span style={{ color: 'var(--danger)' }}>⚠</span> {PERMISSION_LABELS[p] ?? p}
          </label>
        ))}
      </Field>

      <Field
        label={`نطاق المدارس (${form.school_scope.length === 0 ? 'كل المديرية' : `${form.school_scope.length} مدرسة`})`}
        hint="اترك الاختيار فارغًا لمنح نطاق المديرية كاملة"
      >
        <div
          style={{
            maxHeight: 180,
            overflow: 'auto',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 8,
          }}
        >
          {schools.map((s) => (
            <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
              <input
                type="checkbox"
                checked={form.school_scope.includes(s.id)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    school_scope: e.target.checked
                      ? [...f.school_scope, s.id]
                      : f.school_scope.filter((x) => x !== s.id),
                  }))
                }
              />
              {s.name}
            </label>
          ))}
        </div>
      </Field>

      {editing ? (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
          <input
            type="checkbox"
            checked={form.disabled}
            onChange={(e) => setForm((f) => ({ ...f, disabled: e.target.checked }))}
          />
          تعطيل الحساب
        </label>
      ) : null}
    </Modal>
  );
}
